import { Injectable } from '@angular/core';
import { CookieService } from 'ngx-cookie-service';

/** Key for storing token expiry timestamp */
const TOKEN_EXPIRY_KEY = 'token_expiry';

/** Buffer time (in seconds) before actual expiry to trigger refresh/logout */
const EXPIRY_BUFFER_SECONDS = 60;

@Injectable({
  providedIn: 'root',
})
export class CookiesService {
  private readonly localStorageAvailable: boolean;
  private userCookies: string[] = ['token', 'user', TOKEN_EXPIRY_KEY];

  constructor(private cookies: CookieService) {
    this.localStorageAvailable = this.isLocalStorageAvailable();
  }

  /**
   * Checks if local storage is available.
   *
   * @returns True if local storage is available, false otherwise.
   */
  private isLocalStorageAvailable(): boolean {
    try {
      const test = '__localStorageTest__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Gets the value of a cookie or local storage item.
   *
   * @param name - The name of the cookie or local storage item.
   * @returns The value of the cookie or local storage item, or null if not found.
   */
  get(name: string): string | null {
    if (this.localStorageAvailable) {
      const value = localStorage.getItem(name);
      return value !== null ? value : this.cookies.get(name);
    }
    return this.cookies.get(name);
  }

  /**
   * Updates the value of a cookie or local storage item.
   *
   * @param name - The name of the cookie or local storage item.
   * @param value - The new value for the cookie or local storage item.
   */
  update(name: string, value: string): void {
    if (this.localStorageAvailable) {
      localStorage.setItem(name, value);
    } else {
      this.cookies.set(name, value);
    }
  }

  /**
   * Deletes a cookie or local storage item.
   *
   * @param name - The name of the cookie or local storage item.
   */
  delete(name: string): void {
    if (this.localStorageAvailable) {
      localStorage.removeItem(name);
    }
    // Always clear cookies too — prevents stale cookie fallback after localStorage is cleared
    this.cookies.delete(name);
  }

  /**
   * Deletes all user-related cookies.
   */
  deleteUser(): void {
    this.userCookies.forEach(cookie => {
      this.delete(cookie);
    });
  }

  /**
   * Stores the token expiry time based on expires_in value from auth response.
   *
   * @param expiresIn - Token validity duration in seconds
   */
  setTokenExpiry(expiresIn: number): void {
    const expiryTimestamp = Date.now() + (expiresIn * 1000);
    this.update(TOKEN_EXPIRY_KEY, expiryTimestamp.toString());
  }

  /**
   * Gets the stored token expiry timestamp.
   *
   * @returns The expiry timestamp in milliseconds, or null if not set
   */
  getTokenExpiry(): number | null {
    const expiry = this.get(TOKEN_EXPIRY_KEY);
    return expiry ? parseInt(expiry, 10) : null;
  }

  /**
   * Checks if the token is expired or about to expire.
   *
   * @returns true if token is expired or will expire within the buffer period
   */
  isTokenExpired(): boolean {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      // No expiry cookie means we cannot verify locally whether the token is still
      // valid. Treating it as expired is the safe default — it forces the guard to
      // clear the stale token cookie and redirect to login, preventing the blank
      // white screen caused by RedirectLoggedGuard incorrectly treating the user
      // as logged in when the server-side session has already expired.
      return true;
    }

    const bufferMs = EXPIRY_BUFFER_SECONDS * 1000;
    return Date.now() >= (expiry - bufferMs);
  }

  /**
   * Gets the remaining time until token expiry.
   *
   * @returns Remaining time in seconds, or 0 if expired/unknown
   */
  getTokenRemainingTime(): number {
    const expiry = this.getTokenExpiry();
    if (!expiry) {
      return 0;
    }

    const remaining = Math.max(0, expiry - Date.now());
    return Math.floor(remaining / 1000);
  }
}
